import React from 'react';
import { Loader2, Search, X, Send, Paperclip, Mic } from 'lucide-react';

// Import direto (não pelo barril '../common'): o widget é montado em TODA tela via
// DashboardLayout, que já importa deep por esse mesmo motivo — o barril arrastaria
// Modal/StatCard/... para o chunk principal sem ninguém usá-los aqui.
import SearchInput from '../common/SearchInput';
import { BolhaCompacta } from './BolhaCompacta';
import { NAVY, GOLD, fmtHora } from '../../utils/chatUtils';

// LISTA DE CONVERSAS
// min-h-0: sem isto o filho `overflow-y-auto` não encolhe numa coluna flex e a
// lista empurraria o campo de busca para fora do painel em vez de rolar.
export const ListaConversas = ({
  canais, busca, setBusca, loadingCanais, canaisOrdenados, canaisFiltrados, abrirCanal, iconeCanal,
}) => (
  <div className="flex-1 flex flex-col min-h-0">
    {canais.length > 0 && (
      <div className="p-2 border-b border-slate-100 flex-shrink-0">
        <SearchInput value={busca} onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar pessoas ou conversas..." />
      </div>
    )}
    <div className="flex-1 overflow-y-auto">
    {loadingCanais && canais.length === 0 ? (
      <div className="p-6 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>
    ) : canaisOrdenados.length === 0 ? (
      <div className="p-6 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
        <Search size={22} className="opacity-40" /> Nenhuma conversa ainda.
      </div>
    ) : canaisFiltrados.length === 0 ? (
      <div className="p-6 text-center text-sm text-slate-400">Nada encontrado.</div>
    ) : canaisFiltrados.map(c => (
      <button key={c.id} onClick={() => abrirCanal(c)} aria-label={`Abrir ${c.nome}`}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-slate-50 border-b border-slate-100">
        {iconeCanal(c, 38)}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1">
            <span className="font-medium text-sm text-slate-800 truncate">{c.nome}</span>
            {c.ultima_mensagem && <span className="text-2xs text-slate-400 flex-shrink-0">{fmtHora(c.ultima_mensagem.created_at)}</span>}
          </div>
          <div className="flex items-center justify-between gap-1">
            <span className="text-xs text-slate-500 truncate">
              {c.ultima_mensagem
                ? (c.ultima_mensagem.deletado ? 'mensagem apagada' : (c.ultima_mensagem.texto || (c.ultima_mensagem.anexo ? '📎 anexo' : '')))
                : (c.descricao || '')}
            </span>
            {c.nao_lidas > 0 && (
              <span className="text-2xs font-semibold rounded-full px-1.5 py-0.5 min-w-[18px] text-center flex-shrink-0 text-slate-900"
                style={{ background: GOLD }}>{c.nao_lidas}</span>
            )}
          </div>
        </div>
      </button>
    ))}
    </div>
  </div>
);

// CONVERSA
export const ConversaView = ({
  listaRef, msgsEndRef, loadingMsgs, grupos, meuId, usuariosPorId, gravando, pararGravacao,
  fileInputRef, enviarArquivo, iniciarGravacao, inputRef, texto, setTexto, onKeyDown,
  onPasteArquivo, enviarTexto,
}) => (
  <>
    <div ref={listaRef} role="log" aria-label="Mensagens" aria-live="polite"
      className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 bg-slate-50">
      {loadingMsgs ? (
        <div className="text-center text-slate-400 py-8"><Loader2 className="animate-spin mx-auto" /></div>
      ) : grupos.length === 0 ? (
        <div className="text-center text-slate-400 py-8 text-sm">Nenhuma mensagem. Diga olá! 👋</div>
      ) : grupos.map(item => item._sep ? (
        <div key={item.key} className="flex items-center gap-2 my-2">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-2xs text-slate-400 font-medium">{item.label}</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
      ) : (
        <BolhaCompacta key={item.id} msg={item} meuId={meuId} fotoUrl={usuariosPorId[item.autor_id]?.foto_url} />
      ))}
      <div ref={msgsEndRef} />
    </div>

    {/* input */}
    <div className="p-2 border-t border-slate-200 flex items-end gap-1.5 flex-shrink-0 bg-white">
      {gravando ? (
        <div className="flex items-center gap-2 w-full">
          <button onClick={() => pararGravacao(true)} aria-label="Cancelar gravação" title="Cancelar"
            className="p-2 text-slate-500 hover:text-red-500 rounded-lg hover:bg-slate-100 flex-shrink-0"><X size={18} /></button>
          <div className="flex-1 flex items-center gap-2 text-sm text-red-600">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Gravando áudio…
          </div>
          <button onClick={() => pararGravacao(false)} aria-label="Enviar áudio" title="Enviar áudio"
            className="p-2 rounded-lg text-white flex-shrink-0" style={{ background: NAVY }}><Send size={18} /></button>
        </div>
      ) : (
        <>
          <button onClick={() => fileInputRef.current?.click()} aria-label="Anexar arquivo" title="Anexar arquivo"
            className="p-2 text-slate-500 hover:text-primary rounded-lg hover:bg-slate-100 flex-shrink-0">
            <Paperclip size={18} />
          </button>
          <input ref={fileInputRef} type="file" multiple className="hidden" aria-hidden="true" tabIndex={-1}
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = '';                       // permite reescolher o mesmo arquivo
              for (const f of files) await enviarArquivo(f);
            }} />
          <button onClick={iniciarGravacao} aria-label="Gravar áudio" title="Gravar áudio"
            className="p-2 text-slate-500 hover:text-primary rounded-lg hover:bg-slate-100 flex-shrink-0">
            <Mic size={18} />
          </button>
          <label htmlFor="widget-msg-input" className="sr-only">Escrever mensagem</label>
          <textarea id="widget-msg-input" ref={inputRef} rows={1} value={texto}
            onChange={(e) => setTexto(e.target.value)} onKeyDown={onKeyDown} onPaste={onPasteArquivo}
            placeholder="Mensagem..."
            className="flex-1 min-w-0 resize-none px-3 py-2 border border-slate-300 rounded-lg text-sm max-h-24 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          <button onClick={enviarTexto} disabled={!texto.trim()} aria-label="Enviar mensagem"
            className="p-2 rounded-lg text-white disabled:opacity-40 flex-shrink-0" style={{ background: NAVY }}>
            <Send size={18} />
          </button>
        </>
      )}
    </div>
  </>
);
